import { useTranslation } from 'react-i18next';
import { Skeleton } from '@bliks-io/ui';
import { useSuperAdminAiUsage } from '@/pages/admin/api/queries/useSuperAdminAiUsage';
import { formatMicroEur, formatNumber } from '@/lib/formatters';
import { AiUsageSourceTable } from '@/pages/admin/ai-usage/AiUsageSourceTable';
import { AiUsageWarnings } from '@/pages/admin/ai-usage/AiUsageWarnings';
import { totalCallsOf, totalTokensOf } from '@/pages/admin/ai-usage/aiUsageTotals';

interface TenantAiUsageTabProps {
  tenantId: string;
}

function UsageStat({
  label,
  value,
  isLoading
}: {
  label: string;
  value: string;
  isLoading: boolean;
}) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      {isLoading ? (
        <Skeleton className="inline-block h-4 w-16 align-middle" />
      ) : (
        <span className="font-medium tabular-nums">{value}</span>
      )}
    </div>
  );
}

/** All-time per-source AI usage for one tenant; the time-scoped view lives on
 *  the cross-tenant AI usage page. */
export function TenantAiUsageTab({ tenantId }: TenantAiUsageTabProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useSuperAdminAiUsage({ tenantId });

  const summary = data?.summary;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{t('adminSettings.tenant.tabUsage')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('aiUsage.tenantTabDescription')}</p>
      </div>

      {/* On error, render only the message — zeroed stats would misread as
          "no usage". */}
      {isError && <p className="text-sm text-destructive">{t('aiUsage.loadError')}</p>}
      {!isError && (
        <>
          <div className="flex flex-wrap gap-6 text-sm">
            <UsageStat
              label={t('aiUsage.totalCost')}
              value={formatMicroEur(summary?.totalCostMicroEur ?? 0, i18n.language)}
              isLoading={isLoading}
            />
            <UsageStat
              label={t('aiUsage.calls')}
              value={formatNumber(totalCallsOf(summary), i18n.language)}
              isLoading={isLoading}
            />
            <UsageStat
              label={t('aiUsage.tokens')}
              value={formatNumber(totalTokensOf(summary), i18n.language)}
              isLoading={isLoading}
            />
          </div>

          <AiUsageWarnings summary={summary} />

          <AiUsageSourceTable sources={data?.sources ?? []} isLoading={isLoading} />
        </>
      )}
    </div>
  );
}
